/**
 * Cart Actions con integrazione Backend API
 *
 * Actions asincrone per gestire carrello con Laravel backend
 * Usa redux-thunk per async operations
 */

import {
  addToCart as addToCartAPI,
  getCart as getCartAPI,
  getUserCart as getUserCartAPI,
  updateCartItem as updateCartItemAPI,
  removeCartItem as removeCartItemAPI,
  clearCart as clearCartAPI,
  clearUserCart as clearUserCartAPI,
  mergeGuestCart as mergeGuestCartAPI,
} from '../../api/backend';

import { getSessionId, clearSession } from '../../utils/session';

// Action Types
export const LOAD_CART_REQUEST = 'LOAD_CART_REQUEST';
export const LOAD_CART_SUCCESS = 'LOAD_CART_SUCCESS';
export const LOAD_CART_FAILURE = 'LOAD_CART_FAILURE';

export const ADD_TO_CART_REQUEST = 'ADD_TO_CART_REQUEST';
export const ADD_TO_CART_SUCCESS = 'ADD_TO_CART_SUCCESS';
export const ADD_TO_CART_FAILURE = 'ADD_TO_CART_FAILURE';

export const UPDATE_CART_REQUEST = 'UPDATE_CART_REQUEST';
export const UPDATE_CART_SUCCESS = 'UPDATE_CART_SUCCESS';
export const UPDATE_CART_FAILURE = 'UPDATE_CART_FAILURE';

export const REMOVE_FROM_CART_REQUEST = 'REMOVE_FROM_CART_REQUEST';
export const REMOVE_FROM_CART_SUCCESS = 'REMOVE_FROM_CART_SUCCESS';
export const REMOVE_FROM_CART_FAILURE = 'REMOVE_FROM_CART_FAILURE';

export const CLEAR_CART_SUCCESS = 'CLEAR_CART_SUCCESS';

export const MERGE_CART_SUCCESS = 'MERGE_CART_SUCCESS';

// ==================== LOAD CART ====================

/**
 * Carica carrello dal backend
 */
export const loadCart = () => async (dispatch) => {
  try {
    dispatch({ type: LOAD_CART_REQUEST });

    const token = localStorage.getItem('auth_token');
    let cartData;

    if (token) {
      // Utente autenticato: carica carrello per user_id (dopo merge)
      cartData = await getUserCartAPI();
    } else {
      // Guest: carica carrello per session_id
      const sessionId = getSessionId();
      cartData = await getCartAPI(sessionId);
    }

    dispatch({
      type: LOAD_CART_SUCCESS,
      payload: cartData,
    });

    return cartData;
  } catch (error) {
    dispatch({
      type: LOAD_CART_FAILURE,
      payload: error.message,
    });
    console.error('Load cart error:', error);
    throw error;
  }
};

// ==================== ADD TO CART ====================

/**
 * Aggiungi prodotto al carrello
 *
 * @param {number} productId - ID prodotto
 * @param {number} quantity - Quantità (default 1)
 */
export const addToCart = (productId, quantity = 1) => async (dispatch) => {
  try {
    dispatch({ type: ADD_TO_CART_REQUEST });

    // Se autenticato non serve session_id (backend usa user_id dal token)
    const token = localStorage.getItem('auth_token');
    const sessionId = token ? null : getSessionId();
    const response = await addToCartAPI(sessionId, productId, quantity);

    dispatch({
      type: ADD_TO_CART_SUCCESS,
      payload: response.cart_item,
    });

    // Ricarica carrello completo per aggiornare totale
    dispatch(loadCart());

    return response;
  } catch (error) {
    dispatch({
      type: ADD_TO_CART_FAILURE,
      payload: error.message,
    });
    console.error('Add to cart error:', error);
    throw error;
  }
};

// ==================== UPDATE QUANTITY ====================

/**
 * Aggiorna quantità item nel carrello
 *
 * @param {number} itemId - ID cart item
 * @param {number} quantity - Nuova quantità
 */
export const updateCartQuantity = (itemId, quantity) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_CART_REQUEST });

    const response = await updateCartItemAPI(itemId, quantity);

    dispatch({
      type: UPDATE_CART_SUCCESS,
      payload: { itemId, quantity },
    });

    // Ricarica carrello per aggiornare totale
    dispatch(loadCart());

    return response;
  } catch (error) {
    dispatch({
      type: UPDATE_CART_FAILURE,
      payload: error.message,
    });
    console.error('Update cart error:', error);
    throw error;
  }
};

// ==================== REMOVE FROM CART ====================

/**
 * Rimuovi item dal carrello
 *
 * @param {number} itemId - ID cart item
 */
export const removeFromCart = (itemId) => async (dispatch) => {
  try {
    dispatch({ type: REMOVE_FROM_CART_REQUEST });

    await removeCartItemAPI(itemId);

    dispatch({
      type: REMOVE_FROM_CART_SUCCESS,
      payload: itemId,
    });

    // Ricarica carrello
    dispatch(loadCart());

  } catch (error) {
    dispatch({
      type: REMOVE_FROM_CART_FAILURE,
      payload: error.message,
    });
    console.error('Remove from cart error:', error);
    throw error;
  }
};

// ==================== CLEAR CART ====================

/**
 * Svuota carrello completo
 */
export const clearCartAction = () => async (dispatch) => {
  try {
    const token = localStorage.getItem('auth_token');

    if (token) {
      // Utente autenticato: svuota per user_id (endpoint protetto)
      await clearUserCartAPI();
    } else {
      // Guest: svuota per session_id e pulisci localStorage
      const sessionId = getSessionId();
      await clearCartAPI(sessionId);
      clearSession();
    }

    dispatch({ type: CLEAR_CART_SUCCESS });

  } catch (error) {
    console.error('Clear cart error:', error);
    throw error;
  }
};

// ==================== MERGE CART (al login) ====================

/**
 * Merge carrello guest con utente loggato
 * Richiede token auth nei headers (gestito da interceptor)
 */
export const mergeCart = () => async (dispatch) => {
  try {
    const sessionId = getSessionId();
    if (!sessionId) return;

    const response = await mergeGuestCartAPI(sessionId);

    dispatch({
      type: MERGE_CART_SUCCESS,
      payload: response,
    });

    // Ricarica carrello utente
    dispatch(loadCart());

    return response;
  } catch (error) {
    console.error('Merge cart error:', error);
    throw error;
  }
};
